import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { ShopOrderBomDataGridApi } from "src/api/generated/erp/production/api/shopOrderBom/shopOrderBomDataGridApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import {
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { concatWithPipe } from "src/utils/strings.tsx";
import { getAcquisitionMethodLabel } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { getShopOrderLineStateLabels } from "src/api/generated/erp/db/types/enums/shopOrderLineState.ts";
import { getPurchaseOrderLineStateLabel } from "src/api/generated/erp/db/types/enums/purchaseOrderLineState.ts";
import { getShopOrderStateLabel } from "src/api/generated/erp/db/types/enums/shopOrderState.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import {
	faBan,
	faDolly,
	faFileUpload,
	faShare,
	faSitemap,
	faTable,
	faTools,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { FormSingleFileInputField } from "src/components/common/forms/fields/FormSingleFileInputField.tsx";
import { ShopOrderBomActionApi } from "src/api/generated/erp/production/api/shopOrderBom/shopOrderBomActionApi.ts";
import { uploadFile } from "src/utils/fileUploading.ts";
import { TypographyWithLineBreaks } from "src/components/common/typography/TypographyWithLineBreaks.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { OpenPurchaseOrderButton } from "src/components/views/erp/utilComponents/OpenPurchaseOrderButton.tsx";
import { OpenSitePartButton } from "src/components/views/erp/utilComponents/OpenSitePartButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ShopOrderLineConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/ShopOrderLineConfiguratorView.tsx";
import { PartConfigurationDetailsView } from "src/components/views/erp/configurator/inspecting/PartConfigurationDetailsView.tsx";
import { ShopOrderLineForm } from "src/components/views/erp/production/shopOrderBom/ShopOrderLineForm.tsx";
import { ShopOrderBomTreeContainer } from "src/components/views/erp/production/shopOrderBom/ShopOrderBomTreeContainer.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { OpenShopOrdersOfSourceButton } from "src/components/views/erp/utilComponents/OpenShopOrdersOfSourceButton.tsx";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { ShopOrderBomReserveAndPickView } from "src/components/views/erp/production/shopOrderPicking/ShopOrderBomReserveAndPickView.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { cancelShopOrderBomLine } from "src/components/views/erp/production/shopOrderBom/cancelShopOrderBomLine.ts";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { formatDayJs, parseIsoDateNullable } from "src/utils/dayjsUtils.ts";
import { ShopOrderLineView } from "src/api/generated/erp/db/types/tables/shopOrderLineView.ts";
import { createShopOrderLinePartConfiguratorType } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeCreators.ts";
import { getConfiguratorFormDialogTitle } from "src/components/views/erp/configurator/configuratorForm/utils/configuratorFormUtils.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";

export interface ShopOrderBomDataGridProps {
	shopOrderId: number;
	editingEnabled?: boolean;
	filterByShopOrderOperationId?: number | null;
}

export const ShopOrderBomDataGrid = ({
	shopOrderId,
	editingEnabled: editingEnabledProp,
	filterByShopOrderOperationId,
}: ShopOrderBomDataGridProps) => {
	const showInputForm = useFormInput();
	const showConfirmDialog = useConfirmDialog();
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();

	const editingEnabled = editingEnabledProp ?? hasPermission(Permissions.Production.ShopOrders.MANAGE);

	return (
		<ClientSideDataGridModel
			gridId={"F600FC6946D43A77"}
			fetchData={async ({ searchQuery, showFullBom, showCancelledLines }) =>
				await ShopOrderBomDataGridApi.getGridData({
					searchQuery: searchQuery,
					shopOrderId: shopOrderId,
					includeCancelledLines: showCancelledLines,
					filterByShopOrderOperationId: showFullBom ? null : filterByShopOrderOperationId,
				})
			}
			initialParams={{
				searchQuery: "",
				showFullBom: false,
				showCancelledLines: false,
			}}
			storedParams={["showFullBom"]}
			getRows={(data) => data.lines}
			getRowId={(row) => row.shopOrderLineId}
			render={({ data, dataGridProps, onlySelectedRow, refreshData, currentParams }) => {
				const shopOrderIsClosed = data.shopOrderState === "READY" || data.shopOrderState === "CANCELLED";

				const shopOrderIsAtLeastStarted = shopOrderIsClosed || data.shopOrderState === "STARTED";

				return (
					<FormCrudDataGrid<ShopOrderLineView>
						columns={[
							integerColumn({
								field: "referenceNo",
								headerName: i18n.t("reference_no"),
								width: 70,
							}),
							textColumn({
								field: "configurationOrPartDescription",
								headerName: i18n.t("part"),
								width: 200,
							}),
							floatColumn({
								field: "quantity",
								headerName: i18n.t("quantity"),
								width: 70,
							}),
							textColumn({
								field: "partUnit",
								headerName: i18n.t("unit"),
								width: 70,
							}),
							textColumn({
								field: "operationId",
								headerName: i18n.t("operation"),
								valueGetter: (_, row) => concatWithPipe(row.operationNo, row.operationDescription),
								width: 150,
							}),
							textColumn({
								field: "acquisitionMethod",
								headerName: i18n.t("acquisition_method"),
								valueGetter: (_, row) => {
									const methodLabel = getAcquisitionMethodLabel(row.acquisitionMethod);
									const purchaseOrderStateLabel =
										row.acquisitionPurchaseOrderLineState != null ?
											getPurchaseOrderLineStateLabel(row.acquisitionPurchaseOrderLineState)
										:	null;
									const shopOrderStateLabel =
										row.acquisitionShopOrderState != null ?
											getShopOrderStateLabel(row.acquisitionShopOrderState)
										:	null;
									const purchaseOrderArrivalDate =
										row.acquisitionPurchaseOrderPlannedArrivalDate != null ?
											formatDayJs(
												parseIsoDateNullable(row.acquisitionPurchaseOrderPlannedArrivalDate),
											)
										:	null;
									return (
										row.acquisitionShopOrderId != null ?
											`${methodLabel} (${[row.acquisitionShopOrderId, shopOrderStateLabel]
												.filter((value) => value != null && value !== "")
												.join(", ")})`
										: row.acquisitionPurchaseOrderId != null ?
											`${methodLabel} (${[
												row.acquisitionPurchaseOrderId,
												purchaseOrderStateLabel,
												purchaseOrderArrivalDate,
											]
												.filter((value) => value != null && value !== "")
												.join(", ")})`
										:	`${methodLabel}`
									);
								},
							}),
							textColumn({
								field: "note",
								headerName: i18n.t("note"),
							}),
							floatColumn({
								field: "reservedQuantity",
								headerName: i18n.t("reserved_quantity"),
							}),
							floatColumn({
								field: "pickedQuantity",
								headerName: i18n.t("picked_quantity"),
								width: 120,
							}),
							enumColumn({
								field: "shopOrderLineState",
								headerName: i18n.t("state"),
								enumLabels: getShopOrderLineStateLabels(),
							}),
							textColumn({
								field: "supplierName",
								headerName: i18n.t("supplier"),
							}),
							moneyColumn({
								field: "unitCost",
								headerName: i18n.t("unit_cost"),
							}),
							moneyColumn({
								field: "totalCost",
								headerName: i18n.t("total_cost"),
							}),
							dateTimeColumn({
								field: "releasedAt",
								headerName: i18n.t("released_at"),
							}),
							dateTimeColumn({
								field: "cancelledDate",
								headerName: i18n.t("cancelled_date"),
							}),
							integerColumn({
								field: "shopOrderLineId",
								headerName: i18n.t("id"),
							}),
						]}
						actionBarComponents={
							<>
								<DocumentsOfObjectButton
									objectRef={nullableAavoObjectRef(
										"SHOP_ORDER_LINE",
										onlySelectedRow?.shopOrderLineId,
									)}
									editable={editingEnabled}
								/>
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={(value) => refreshData({ searchQuery: value })}
								/>
								<AsyncButton
									key={"reserveAndPick"}
									icon={faDolly}
									label={i18n.t("reserve_and_pick")}
									variant={"outlined"}
									onClick={() => {
										openDialog(() => ({
											title: i18n.t("reserve_and_pick_materials"),
											size: "lg",
											agentContext: {
												key: defineAgentUiContextKey("erp.production.shopOrderBomReserveAndPick.dialog"),
												title: i18n.t("reserve_and_pick_materials"),
												pathSegments: [`${i18n.t("shop_order")} ${shopOrderId}`],
												data: { shopOrderId },
											},
											content: <ShopOrderBomReserveAndPickView shopOrderId={shopOrderId} />,
										}));
									}}
								/>
								{hasPermission(Permissions.Production.ShopOrders.READ) && (
									<AsyncButton
										label={i18n.t("tree_view")}
										icon={faSitemap}
										variant={"outlined"}
										onClick={() => {
											openDialog(() => ({
												title: i18n.t("tree_view"),
												agentContext: {
													key: defineAgentUiContextKey("erp.production.shopOrderBomTree.shopOrderBomDataGrid.dialog"),
													title: i18n.t("tree_view"),
													pathSegments: [`${i18n.t("shop_order")} ${shopOrderId}`],
													data: { shopOrderId },
												},
												content: <ShopOrderBomTreeContainer shopOrderId={shopOrderId} />,
											}));
										}}
									/>
								)}
								{filterByShopOrderOperationId != null && (
									<AavoCheckbox
										label={i18n.t("full_bom")}
										checked={currentParams.showFullBom}
										onChange={async (value) => await refreshData({ showFullBom: value })}
									/>
								)}
							</>
						}
						actionBarMenuComponents={[
							<MenuCheckbox
								key={"showCancelled"}
								label={i18n.t("show_cancelled")}
								checked={currentParams.showCancelledLines}
								onChange={(value) => refreshData({ showCancelledLines: value })}
							/>,
							editingEnabled && (
								<AsyncMenuButton
									key={"import"}
									label={i18n.t("import_bom")}
									icon={faFileUpload}
									onClick={async () => {
										const formInput = await showInputForm<{ file: File }>({
											title: i18n.t("import_bom"),
											content: ({ control }) => (
												<>
													<FormSingleFileInputField
														control={control}
														name={"file"}
														label={i18n.t("file")}
														accept={".csv"}
													/>
													<TypographyWithLineBreaks variant={"body2"}>
														{i18n.t("import_shop_order_bom_dialog_helper_text")}
													</TypographyWithLineBreaks>
												</>
											),
										});
										if (formInput == null) return;
										await ShopOrderBomActionApi.importShopOrderBom({
											shopOrderId: shopOrderId,
											file: await uploadFile(formInput.file),
										});
										await refreshData();
									}}
								/>
							),
						]}
						rowContextMenuComponents={({ row, allSelectedRows, onlySingleRowSelected }) => [
							<CustomUiActionObjectMenuItems
								key={"customFunctionButtons"}
								view={"SHOP_ORDER_LINES"}
								objectIds={allSelectedRows.map((line) => line.shopOrderLineId)}
							/>,
							editingEnabled && row.shopOrderLineState === "INITIAL" && !shopOrderIsClosed && (
								<AsyncMenuButton
									key={"release"}
									label={i18n.t("release")}
									icon={faShare}
									onClick={async () => {
										const confirmed = await showConfirmDialog({
											message:
												allSelectedRows.length > 1 ?
													i18n.t("confirm_release_shop_order_lines")
												:	i18n.t("confirm_release_shop_order_line"),
										});
										if (!confirmed) return;

										await ShopOrderBomActionApi.releaseShopOrderLines({
											shopOrderLineIds: allSelectedRows.map((r) => r.shopOrderLineId),
										});
										await refreshData();
									}}
								/>
							),
							editingEnabled && row.shopOrderLineState === "RELEASED" && onlySingleRowSelected && (
								<AsyncMenuButton
									key={"revertRelease"}
									label={i18n.t("revert_release")}
									disabled={shopOrderIsAtLeastStarted}
									icon={faUndo}
									onClick={async () => {
										const confirmed = await showConfirmDialog({
											message: i18n.t("confirm_revert_release_shop_order_line"),
										});
										if (!confirmed) return;

										await ShopOrderBomActionApi.revertReleaseShopOrderLine({
											shopOrderLineId: row.shopOrderLineId,
											shopOrderLineLastUpdated: row.lastUpdated,
										});
										await refreshData();
									}}
								/>
							),
							onlySingleRowSelected && <OpenSitePartButton partId={row.partId} key={"part"} />,
							row.acquisitionPurchaseOrderId != null && onlySingleRowSelected && (
								<OpenPurchaseOrderButton
									key={"purchaseOrder"}
									purchaseOrderId={row.acquisitionPurchaseOrderId}
								/>
							),
						onlySingleRowSelected &&
							row.acquisitionMethod === "MANUFACTURE" &&
							row.shopOrderLineState !== "INITIAL" && (
								<OpenShopOrdersOfSourceButton
									key={"openShopOrder"}
									shopOrderSource={{
										sourceType: "SHOP_ORDER_LINE",
										sourceId: row.shopOrderLineId,
									}}
								/>
							),
							row.shopOrderLineId != null && editingEnabled && onlySingleRowSelected && (
								<AsyncMenuButton
									key={"cancel"}
									label={i18n.t("cancel")}
									icon={faBan}
									onClick={async () => {
										await cancelShopOrderBomLine({
											shopOrderLineId: row.shopOrderLineId,
											showConfirmDialog: showConfirmDialog,
										});
										await refreshData();
									}}
								/>
							),
							row.partIsConfigurable && editingEnabled && onlySingleRowSelected && (
								<AsyncMenuButton
									key={"configure"}
									label={i18n.t("configure")}
									icon={faTools}
									disabled={row.shopOrderLineState !== "INITIAL"}
									onClick={async () => {
										const configuratorType = createShopOrderLinePartConfiguratorType(
											row.shopOrderLineId,
										);
										const dialogTitle = await getConfiguratorFormDialogTitle({
											configuratorType: configuratorType,
										});
										openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
											title: dialogTitle,
											agentContext: {
												key: defineAgentUiContextKey("erp.production.shopOrderLineConfiguration.dialog"),
												title: i18n.t("configuration"),
												pathSegments: [`${i18n.t("shop_order_line")} ${row.shopOrderLineId}`],
												data: {
													shopOrderId,
													shopOrderLineId: row.shopOrderLineId,
													partConfigurationId: row.partConfigurationId ?? null,
												},
											},
											content: (
												<ShopOrderLineConfiguratorView
													configuratorType={configuratorType}
													closeDialog={closeDialog}
													refreshSourceView={refreshData}
													onDataDirtyStateChanged={onDataDirtyStateChanged}
												/>
											),
										}));
									}}
								/>
							),
							row.partConfigurationId != null && onlySingleRowSelected && (
								<AsyncMenuButton
									key={"configuration"}
									label={i18n.t("configuration")}
									icon={faTable}
									onClick={() => {
										openDialog(() => ({
											title: i18n.t("configuration"),
											agentContext: {
												key: defineAgentUiContextKey("erp.production.shopOrderLineConfigurationDetails.dialog"),
												title: i18n.t("configuration"),
												pathSegments: [`${i18n.t("shop_order_line")} ${row.shopOrderLineId}`],
												data: {
													shopOrderId,
													shopOrderLineId: row.shopOrderLineId,
													partConfigurationId: row.partConfigurationId ?? null,
												},
											},
											content: (
												<PartConfigurationDetailsView
													partConfigurationId={row.partConfigurationId!}
													partConfiguratorType={{
														type: "shopOrderLine",
														shopOrderLineId: row.shopOrderLineId,
														explicitPartConfigurationId: null,
													}}
													showBom={true}
												/>
											),
										}));
									}}
								/>
							),
							onlySingleRowSelected && (
								<OpenObjectChangeLogButton
									key={"changeLog"}
									objectRef={{
										objectType: "SHOP_ORDER_LINE",
										objectId: row.shopOrderLineId,
									}}
								/>
							),
						]}
						form={{
							addRowEnabled: !shopOrderIsClosed && editingEnabled,
							editEnabled: !shopOrderIsClosed && editingEnabled,
							dialogSize: "sm",
							dialogTitle: i18n.t("shop_order_line"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.production.shopOrderLine.formDialog"),
								title: i18n.t("shop_order_line"),
								data: {
									shopOrderId,
									shopOrderLineId: row?.shopOrderLineId ?? null,
									partId: row?.partId ?? null,
									operationId: row?.operationId ?? null,
								},
							}),
							component: ({ row, ...other }) => (
								<ShopOrderLineForm
									shopOrderId={shopOrderId}
									shopOrderLineId={row?.shopOrderLineId ?? null}
									{...other}
								/>
							),
						}}
						remove={{
							type: !shopOrderIsClosed && editingEnabled ? "enabled" : "disabled",
							action: async ({ items }) => {
								await ShopOrderBomActionApi.delete_({
									shopOrderLines: items,
								});
								await refreshData();
							},
						}}
						agentContext={{
							key: defineAgentUiContextKey("erp.production.shopOrderBom.grid"),
							data: {
								shopOrderId,
								filterByShopOrderOperationId: filterByShopOrderOperationId ?? null,
								showFullBom: currentParams.showFullBom,
							},
							rowFields: ["shopOrderLineId", "partId", "operationId", "acquisitionMethod"],
							selectedRowPathSegment: (row) => `${i18n.t("shop_order_line")} ${row.shopOrderLineId}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
